/**
 * Fit guide.
 *
 * Turns one number into a filtered result set. The point is not the advice —
 * it is that the advice ends on sticks you can buy.
 *
 * Filter parameter names and the real flex values come from Liquid via data
 * attributes rather than being hard-coded here, because the height parameter is
 * derived from the option name and is not worth typing by hand:
 *   filter.v.option.height (inches, measured from floor to butt-end)
 *
 * Without JS the panels still render, the numbers show sensible defaults, and
 * every button is a plain link to the collection.
 */
(() => {
  'use strict';

  const root = document.querySelector('[data-fit]');
  if (!root) return;

  const base = root.dataset.collection || '/collections/all';
  const flexParam = root.dataset.flexParam;
  const heightParam = root.dataset.heightParam;
  const curveParam = root.dataset.curveParam;

  const flexValues = (root.dataset.flexValues || '')
    .split(',')
    .map((v) => parseFloat(String(v).replace(/[^0-9.]/g, '')))
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);

  const uniq = [...new Set(flexValues)];

  /** Always in stock — browsing this store never shows sold items. */
  const buildUrl = (param, values) => {
    if (!param || !values.length) return null;
    const p = new URLSearchParams();
    values.forEach((v) => p.append(param, v));
    p.append('filter.v.availability', '1');
    return `${base}?${p}`;
  };

  const flash = (el) => {
    if (!el) return;
    el.classList.add('is-updated');
    setTimeout(() => el.classList.remove('is-updated'), 400);
  };

  /* ------------------------------------------------------------- flex --- */

  const weightNum = root.querySelector('[data-fit-weight]');
  const weightRange = root.querySelector('[data-fit-weight-range]');
  const flexOut = root.querySelector('[data-fit-flex-out]');
  const flexValueEl = root.querySelector('[data-fit-flex-value]');
  const flexLink = root.querySelector('[data-fit-flex-link]');

  function updateFlex(weight) {
    // The long-standing rule of thumb: flex is about half body weight.
    const target = Math.round(weight / 2);

    // Snap to values the catalogue actually stocks, and take a band around the
    // target rather than one number, because nobody is precisely one flex.
    const inBand = uniq.filter((v) => Math.abs(v - target) <= 8);
    const chosen = inBand.length
      ? inBand
      : uniq.length
        ? [uniq.reduce((a, b) => (Math.abs(b - target) < Math.abs(a - target) ? b : a))]
        : [];

    if (flexValueEl) {
      flexValueEl.textContent = chosen.length
        ? chosen.length === 1
          ? `${chosen[0]}`
          : `${chosen[0]} – ${chosen[chosen.length - 1]}`
        : `${target}`;
    }
    flash(flexOut);

    const url = buildUrl(flexParam, chosen);
    if (flexLink) {
      if (url) {
        flexLink.href = url;
        flexLink.removeAttribute('aria-disabled');
        flexLink.textContent = `Shop ${flexValueEl.textContent} flex, in stock`;
      } else {
        // Flex is not registered as a filter yet; do not promise a filtered page.
        flexLink.href = base;
        flexLink.textContent = 'Shop sticks in stock';
      }
    }
  }

  /* ----------------------------------------------------------- length --- */

  const heightNum = root.querySelector('[data-fit-height]');
  const heightRange = root.querySelector('[data-fit-height-range]');
  const heightOut = root.querySelector('[data-fit-height-out]');
  const heightValueEl = root.querySelector('[data-fit-height-value]');
  const heightLink = root.querySelector('[data-fit-height-link]');

  function updateHeight(inches) {
    // Butt-end between chin and nose in shoes is roughly 83–88% of height.
    const lo = Math.round(inches * 0.83 * 2) / 2;
    const hi = Math.round(inches * 0.88 * 2) / 2;

    if (heightValueEl) heightValueEl.textContent = `${lo} – ${hi}`;
    flash(heightOut);

    // Heights are stored inconsistently as 65 and 65.0, so offer both forms.
    const values = [];
    for (let v = lo; v <= hi; v += 0.5) {
      const s = Number.isInteger(v) ? String(v) : v.toFixed(1);
      values.push(s);
      if (Number.isInteger(v)) values.push(v.toFixed(1));
    }

    const url = buildUrl(heightParam, values);
    if (heightLink) {
      if (url) {
        heightLink.href = url;
        heightLink.textContent = `Shop ${lo}–${hi}" sticks, in stock`;
      } else {
        heightLink.href = base;
        heightLink.textContent = 'Shop sticks in stock';
      }
    }
  }

  /* ------------------------------------------------------------ curve --- */

  root.querySelectorAll('[data-fit-curve]').forEach((el) => {
    const code = el.getAttribute('data-fit-curve');
    const url = buildUrl(curveParam, [code]);
    if (url) el.href = url;
  });

  /* ------------------------------------------------------------ wiring --- */

  const pair = (numEl, rangeEl, fn) => {
    if (!numEl || !rangeEl) return;
    const sync = (from, to) => {
      to.value = from.value;
      fn(parseFloat(from.value));
    };
    rangeEl.addEventListener('input', () => sync(rangeEl, numEl));
    numEl.addEventListener('input', () => sync(numEl, rangeEl));
    fn(parseFloat(numEl.value));
  };

  pair(weightNum, weightRange, updateFlex);
  pair(heightNum, heightRange, updateHeight);
})();
